import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import FrostedPanel from '../../molecules/FrostedPanel';
import UserCardAvatar from '../../molecules/UserCardAvatar';
import CardActionsMenu from '../../molecules/CardActionsMenu';
import ConfirmDialog from '../../molecules/ConfirmDialog';
import PostVisibilityControl from '../../molecules/PostVisibilityControl';
import { UOM_VISIBILITY_OPTIONS } from '../../molecules/PostVisibilityControl/options';
import ContentWithLinkPreviews from '../../molecules/ContentWithLinkPreviews';
import ScheduledEntityDetailModal from '../../molecules/ScheduledEntityDetailModal';
import { useClickableLinks } from '../../../hooks/useClickableLinks';
import { invalidateDiscoverMarkersCache } from '../../../hooks/useDiscoverMarkers';
import useLocaleDateFormatter from '../../../hooks/useLocaleDateFormatter';
import { eventService } from '../../../services/eventService';
import { ACTIVE_TAB_STORAGE_KEY, NAVIGATE_TAB_EVENT, TAB_IDS } from '../../../constants/navigation';
import classNames from '../../../utils/classNames';
import { createDiscoverEditIntent } from '../../../utils/discoverComposerIntent';
import { normalizeAttendanceState, resolveActiveRsvpStatus } from '../../../utils/scheduledEntity';
import { resolveEventHostPresentation } from '../../../utils/eventPresentation';
import { formatLastSeenRelative } from '../../../utils/presenceUtils';
import { buildBandPath } from '../../../utils/bandRoutes';
import { isSuggestedFeedItem } from '../../../utils/feedSuggestion';
import bandIcon from '../../../images/band-icon.svg';
import './index.scss';
import '../PostCard/index.scss';

const RSVP_ACTIONS = Object.freeze([
    { status: 'GOING', translationKey: 'home.postCard.relatedObject.actions.going' },
    { status: 'INTERESTED', translationKey: 'home.postCard.relatedObject.actions.interested' },
    { status: 'DECLINED', translationKey: 'home.postCard.relatedObject.actions.declined' },
]);
const ENTITY_VISIBILITY_VALUES = new Set(UOM_VISIBILITY_OPTIONS.map((option) => option.value));

function resolveScheduledTypeLabelKey(objectType) {
    return objectType === 'SESSION'
        ? 'home.postCard.relatedObject.type.session'
        : 'home.postCard.relatedObject.type.event';
}

function resolveScheduledStatusKey(status) {
    return `home.postCard.relatedObject.status.${String(status || 'unknown').trim().toLowerCase()}`;
}

function isScheduledType(objectType) {
    return objectType === 'EVENT' || objectType === 'SESSION';
}

function buildLocationLine(location) {
    if (!location || typeof location !== 'object') {
        return '';
    }
    const parts = [
        location.locationName,
        location.addressLine,
        location.city,
        location.stateOrRegion,
        location.country,
    ].filter(Boolean);
    return parts.join(', ');
}

function readText(value) {
    return typeof value === 'string' ? value.trim() : '';
}

function resolveBandAvatarUrl(item) {
    return readText(item?.profilePicture)
        || readText(item?.avatarUrl)
        || readText(item?.imageUrl)
        || readText(item?.avatar?.url)
        || readText(item?.images?.avatar?.url);
}

function buildInitialAttendance(item) {
    if (!isScheduledType(item?.objectType)) {
        return null;
    }
    return {
        id: item.id,
        objectType: item.objectType,
        scheduledStatus: item.status,
        joinPolicy: item.joinPolicy,
        viewerAttendanceStatus: item.viewerAttendanceStatus,
        viewerInvitedByOrganizer: item.viewerInvitedByOrganizer === true,
        organizerInviteCount: Number.isFinite(item.organizerInviteCount) ? item.organizerInviteCount : null,
        attendanceSummary: item.attendanceSummary || {
            going: 0,
            interested: 0,
            pending: 0,
        },
    };
}

function buildInitialSummary(item) {
    if (!isScheduledType(item?.objectType)) {
        return null;
    }

    const location = item?.location && typeof item.location === 'object'
        ? item.location
        : {};

    return {
        title: item?.title,
        description: item?.description,
        status: item?.status,
        visibility: item?.visibility,
        timezone: item?.timezone,
        startAt: item?.startAt,
        endAt: item?.endAt,
        joinPolicy: item?.joinPolicy,
        attendeeLimit: item?.attendeeLimit,
        organizerInviteCount: item?.organizerInviteCount,
        viewerInvitedByOrganizer: item?.viewerInvitedByOrganizer === true,
        attendanceSummary: item?.attendanceSummary,
        organizer: item?.organizer,
        venue: item?.venue,
        location: {
            locationName: location.locationName ?? item?.locationName,
            addressLine: location.addressLine ?? item?.addressLine,
            city: location.city ?? item?.city,
            stateOrRegion: location.stateOrRegion ?? item?.stateOrRegion,
            country: location.country ?? item?.country,
            countryCode: location.countryCode ?? item?.countryCode,
            latitude: location.latitude ?? item?.latitude,
            longitude: location.longitude ?? item?.longitude,
        },
        updatedAt: item?.updatedAt,
        createdAt: item?.createdAt,
    };
}

function resolveEntityVisibility(value) {
    const normalized = typeof value === 'string' ? value.trim().toUpperCase() : '';
    return ENTITY_VISIBILITY_VALUES.has(normalized) ? normalized : 'PUBLIC';
}

function buildDeleteMessage(t, item) {
    const base = t('home.timelineEntityCard.deleteDialog.base');
    const guidance = t('home.timelineEntityCard.deleteDialog.guidance');
    const defaultMessage = t('home.timelineEntityCard.deleteDialog.default', { base, guidance });
    const displayName = typeof item?.title === 'string' && item.title.trim()
        ? item.title.trim()
        : (typeof item?.name === 'string' && item.name.trim() ? item.name.trim() : '');

    if (!displayName) {
        return defaultMessage;
    }

    return t('home.timelineEntityCard.deleteDialog.withName', {
        base,
        name: displayName,
        guidance,
    });
}

export default function TimelineEntityCard({
    item,
    currentUserId = null,
    dropdownOpenId = null,
    onToggleDropdown = null,
    onDeleteEntity = null,
    onEditEntity = null,
    readOnly = false,
    socialFooter = null,
}) {
    const { t } = useTranslation();
    const { formatDateTime, formatDate, formatTime } = useLocaleDateFormatter();
    const navigate = useNavigate();
    const renderContentWithLinks = useClickableLinks();
    const isScheduled = isScheduledType(item?.objectType);
    const isVenue = item?.objectType === 'VENUE';
    const isBand = item?.objectType === 'BAND';
    const isSuggested = isSuggestedFeedItem(item);
    const isMenuOpen = dropdownOpenId === item?.id;
    const canManageEntity = !readOnly
        && !isBand
        && item?.canManage === true
        && typeof onToggleDropdown === 'function';
    const cardClassName = classNames('post-card', 'timeline-entity-card', {
        'post-card--menu-open': isMenuOpen,
    });
    const itemAttendanceGoing = item?.attendanceSummary?.going;
    const itemAttendanceInterested = item?.attendanceSummary?.interested;
    const itemAttendancePending = item?.attendanceSummary?.pending;
    const propAttendance = useMemo(
        () => buildInitialAttendance({
            id: item?.id,
            objectType: item?.objectType,
            status: item?.status,
            joinPolicy: item?.joinPolicy,
            viewerAttendanceStatus: item?.viewerAttendanceStatus,
            attendanceSummary: {
                going: itemAttendanceGoing,
                interested: itemAttendanceInterested,
                pending: itemAttendancePending,
            },
        }),
        [
            item?.id,
            item?.objectType,
            item?.status,
            item?.joinPolicy,
            item?.viewerAttendanceStatus,
            itemAttendanceGoing,
            itemAttendanceInterested,
            itemAttendancePending,
        ],
    );
    const detailInitialSummary = useMemo(() => buildInitialSummary(item), [item]);
    const visibilityValue = useMemo(() => resolveEntityVisibility(item?.visibility), [item?.visibility]);

    const [attendance, setAttendance] = useState(() => propAttendance);
    const [isRsvpSubmitting, setIsRsvpSubmitting] = useState(false);
    const [rsvpError, setRsvpError] = useState('');
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    useEffect(() => {
        setAttendance(propAttendance);
    }, [propAttendance]);

    const hostPresentation = useMemo(
        () => {
            if (isBand) {
                return {
                    displayName: readText(item?.name)
                        || readText(item?.title)
                        || t('bands.common.band', 'Band'),
                    avatarUrl: resolveBandAvatarUrl(item),
                    objectType: 'BAND',
                    objectId: readText(item?.id) || null,
                };
            }
            return resolveEventHostPresentation(t, item, {
                unknownKey: 'home.postCard.relatedObject.authorUnknown',
            });
        },
        [isBand, item, t],
    );
    const displayName = hostPresentation.displayName;
    const avatarUrl = hostPresentation.avatarUrl || null;
    const isBandHost = hostPresentation.objectType === 'BAND';
    const formattedTimestamp = useMemo(
        () => formatDateTime(item?.createdAt, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }),
        [formatDateTime, item?.createdAt],
    );
    const compactTimestamp = useMemo(() => {
        if (!item?.createdAt) {
            return '';
        }
        const datePart = formatDate(item.createdAt, { year: undefined, month: 'short' });
        const timePart = formatTime(item.createdAt);
        if (datePart && timePart) {
            return `${datePart} · ${timePart}`;
        }
        return datePart || timePart || '';
    }, [formatDate, formatTime, item?.createdAt]);
    const relativeTimestamp = useMemo(
        () => (item?.createdAt ? formatLastSeenRelative(item.createdAt) : ''),
        [item?.createdAt],
    );
    const timestampLabel = useMemo(
        () => (formattedTimestamp ? t('home.postCard.timestamp', { timestamp: formattedTimestamp }) : ''),
        [formattedTimestamp, t],
    );
    const relatedTypeLabel = isBand
        ? t('bands.common.band', 'Band')
        : (
            isScheduled
                ? t(resolveScheduledTypeLabelKey(item.objectType), item.objectType === 'SESSION' ? 'Session' : 'Event')
                : t('common.forms.createEvent.venueLabel', 'Venue')
        );
    const relatedStatusLabel = isScheduled
        ? t(resolveScheduledStatusKey(item.status), item.status || t('home.postCard.relatedObject.status.unknown', 'Unknown'))
        : null;
    const title = item?.title || item?.name || t('home.postCard.relatedObject.untitled', 'Untitled');
    const contentBody = useMemo(
        () => renderContentWithLinks(item?.description || ''),
        [item?.description, renderContentWithLinks],
    );
    const locationLine = useMemo(() => buildLocationLine(item?.location) || buildLocationLine(item), [item]);
    const startsAtLabel = useMemo(
        () => (
            item?.startAt
                ? formatDateTime(item.startAt, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                })
                : null
        ),
        [formatDateTime, item?.startAt],
    );
    const attendanceSummary = attendance?.attendanceSummary || item?.attendanceSummary || {
        going: 0,
        interested: 0,
        pending: 0,
    };
    const viewerAttendanceStatus = attendance?.viewerAttendanceStatus || item?.viewerAttendanceStatus || null;
    const activeRsvpStatus = resolveActiveRsvpStatus(viewerAttendanceStatus);
    const canMutateRsvp = !readOnly && isScheduled && (attendance?.scheduledStatus || item?.status) === 'PUBLISHED';
    const deleteDialogMessage = useMemo(() => buildDeleteMessage(t, item), [item, t]);
    const bandPath = useMemo(() => (isBand ? buildBandPath(item) : null), [isBand, item]);

    const handleOpenBand = useCallback(() => {
        if (!bandPath) {
            return;
        }
        if (typeof window !== 'undefined') {
            try {
                window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, TAB_IDS.BANDS);
            } catch (_) {
                // ignore storage errors
            }
            window.dispatchEvent(
                new CustomEvent(NAVIGATE_TAB_EVENT, {
                    detail: { tabId: TAB_IDS.BANDS, bandObjectId: item?.id, path: bandPath },
                }),
            );
        }
        navigate(bandPath);
    }, [bandPath, item?.id, navigate]);

    const handleStartEdit = useCallback(() => {
        onToggleDropdown?.(item?.id);

        if (typeof onEditEntity === 'function') {
            onEditEntity(item);
            return;
        }

        const intent = createDiscoverEditIntent({
            objectType: item?.objectType,
            objectId: item?.id,
        });

        if (!intent) {
            return;
        }

        if (typeof window !== 'undefined') {
            try {
                window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, TAB_IDS.DISCOVER);
            } catch (_) {
                // ignore storage errors
            }

            window.dispatchEvent(
                new CustomEvent(NAVIGATE_TAB_EVENT, {
                    detail: { tabId: TAB_IDS.DISCOVER },
                }),
            );
        }

        navigate('/discover', {
            state: {
                discoverComposerIntent: intent,
            },
        });
    }, [item, navigate, onEditEntity, onToggleDropdown]);

    const handleRequestDelete = useCallback(() => {
        onToggleDropdown?.(item?.id);
        setDeleteError('');
        setIsDeleteDialogOpen(true);
    }, [item?.id, onToggleDropdown]);

    const handleCancelDelete = useCallback(() => {
        if (isDeleting) {
            return;
        }
        setDeleteError('');
        setIsDeleteDialogOpen(false);
    }, [isDeleting]);

    const handleConfirmDelete = useCallback(async () => {
        if (!item?.id || !item?.objectType || isDeleting) {
            return;
        }

        setIsDeleting(true);
        setDeleteError('');
        try {
            await eventService.deleteEntity(item.objectType, item.id);
            invalidateDiscoverMarkersCache();
            setIsDeleteDialogOpen(false);
            await Promise.resolve(onDeleteEntity?.(item.id));
        } catch (error) {
            setDeleteError(t(
                'home.timelineEntityCard.deleteDialog.error',
                'Failed to delete item.',
            ));
        } finally {
            setIsDeleting(false);
        }
    }, [isDeleting, item?.id, item?.objectType, onDeleteEntity, t]);

    const menuActions = useMemo(() => {
        if (!canManageEntity) {
            return [];
        }
        const actions = [
            {
                id: 'edit',
                label: t('home.postCard.actions.edit'),
                onSelect: handleStartEdit,
            },
        ];

        if (typeof onDeleteEntity === 'function') {
            actions.push({
                id: 'delete',
                label: t('home.postCard.actions.delete'),
                onSelect: handleRequestDelete,
            });
        }

        return actions;
    }, [canManageEntity, handleRequestDelete, handleStartEdit, onDeleteEntity, t]);

    const handleRsvpAction = useCallback(async (status) => {
        if (readOnly || !isScheduled || !canMutateRsvp || isRsvpSubmitting) {
            return;
        }
        if (activeRsvpStatus === status) {
            return;
        }
        setIsRsvpSubmitting(true);
        setRsvpError('');
        try {
            const response = await eventService.upsertAttendance(item.objectType, item.id, status);
            const normalized = normalizeAttendanceState(response, {
                objectType: item.objectType,
                objectId: item.id,
                scheduledStatus: item.status,
                joinPolicy: item.joinPolicy,
                organizerInviteCount: attendance?.organizerInviteCount ?? item.organizerInviteCount,
            });
            if (!normalized) {
                throw new Error('Invalid attendance response');
            }
            setAttendance(normalized);
        } catch (error) {
            setRsvpError(t(
                'home.postCard.relatedObject.rsvpError',
                'We could not update your RSVP. Please retry.',
            ));
        } finally {
            setIsRsvpSubmitting(false);
        }
    }, [
        canMutateRsvp,
        isRsvpSubmitting,
        isScheduled,
        item?.id,
        item?.joinPolicy,
        item?.organizerInviteCount,
        item?.objectType,
        item?.status,
        t,
        activeRsvpStatus,
        attendance?.organizerInviteCount,
        readOnly,
    ]);

    return (
        <>
            <FrostedPanel tone="primary" depth="lg" className={cardClassName}>
                <header className="post-card__header">
                    <UserCardAvatar
                        userId={isBandHost ? null : item?.ownerId}
                        username={displayName}
                        avatarUrl={isBandHost ? undefined : (avatarUrl || undefined)}
                        resolvedAvatar={isBandHost ? {
                            avatarUrl: avatarUrl || undefined,
                            shouldUseSupportAvatar: false,
                        } : undefined}
                        enableProfilePreview={!isBandHost}
                        fallbackSrc={isBandHost ? bandIcon : undefined}
                        radius={isBandHost ? '14px' : undefined}
                        alt={`${displayName || (isBandHost ? 'Band' : 'User')} ${isBandHost ? 'image' : 'avatar'}`}
                        className={classNames('post-card__avatar', {
                            'post-card__avatar--band': isBandHost,
                        })}
                    />
                    <div className="post-card__profile">
                        <div className="post-card__identity">
                            <span className="post-card__display-name">{displayName}</span>
                            <span
                                className={classNames('post-card__badge', {
                                    'post-card__badge--spotlight': isSuggested,
                                })}
                            >
                                {isSuggested && (
                                    <svg
                                        className="post-card__badge-icon"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-hidden="true"
                                    >
                                        <path d="M12 2l2.6 6.6L22 10l-5.5 4.7L18.2 22 12 18l-6.2 4 1.7-7.3L2 10l7.4-1.4L12 2z" />
                                    </svg>
                                )}
                                {isSuggested
                                    ? t('home.postCard.badges.suggestedForYou')
                                    : t('home.postCard.badges.default')}
                            </span>
                            <span
                                className={classNames('post-card__badge', {
                                    'post-card__badge--event': item?.objectType === 'EVENT',
                                    'post-card__badge--session': item?.objectType === 'SESSION',
                                    'post-card__badge--venue': isVenue,
                                    'post-card__badge--band': isBand,
                                })}
                            >
                                <svg
                                    className="post-card__badge-icon"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                >
                                    {isBand ? (
                                        <>
                                            <path d="M9 18V5l12-2v13" />
                                            <circle cx="6" cy="18" r="3" />
                                            <circle cx="18" cy="16" r="3" />
                                        </>
                                    ) : isVenue ? (
                                        <>
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z" />
                                            <circle cx="12" cy="10" r="3" />
                                        </>
                                    ) : (
                                        <>
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                            <line x1="16" y1="2" x2="16" y2="6" />
                                            <line x1="8" y1="2" x2="8" y2="6" />
                                            <line x1="3" y1="10" x2="21" y2="10" />
                                        </>
                                    )}
                                </svg>
                                {relatedTypeLabel}
                            </span>
                        </div>
                        <div className="post-card__meta">
                            {socialFooter ? null : (
                                <PostVisibilityControl
                                    value={visibilityValue}
                                    onChange={() => {}}
                                    disabled
                                    size="sm"
                                    className="post-card__visibility-control"
                                    options={UOM_VISIBILITY_OPTIONS}
                                />
                            )}
                            {compactTimestamp && (
                                <>
                                    {!socialFooter && <span className="post-card__meta-dot" aria-hidden="true" />}
                                    <span
                                        className="post-card__timestamp"
                                        title={formattedTimestamp || undefined}
                                        aria-label={timestampLabel || formattedTimestamp || undefined}
                                    >
                                        {compactTimestamp}
                                    </span>
                                </>
                            )}
                            {relativeTimestamp && (
                                <>
                                    <span className="post-card__meta-dot" aria-hidden="true" />
                                    <span className="post-card__timestamp post-card__timestamp--relative">
                                        {relativeTimestamp}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    {menuActions.length > 0 && (
                        <CardActionsMenu
                            isOpen={isMenuOpen}
                            onToggle={onToggleDropdown}
                            itemId={item?.id}
                            actions={menuActions}
                            disabled={isDeleting}
                        />
                    )}
                </header>

                <div className="post-card__content">
                    {item?.description && (
                        <ContentWithLinkPreviews previewUrls={contentBody.previews}>
                            <div className="post-card__text" style={{ whiteSpace: 'pre-wrap' }}>
                                {contentBody.content}
                            </div>
                        </ContentWithLinkPreviews>
                    )}

                    <section
                        className="post-card__related-object"
                        aria-label={isBand
                            ? t('bands.common.bandDetails', 'Band details')
                            : t('home.postCard.relatedObject.aria', 'Related event details')}
                    >
                        <div className="post-card__related-object-header">
                            <span className="post-card__related-object-type">{relatedTypeLabel}</span>
                            {relatedStatusLabel && (
                                <span className="post-card__related-object-status">{relatedStatusLabel}</span>
                            )}
                        </div>
                        {isScheduled ? (
                            <button
                                type="button"
                                className="post-card__related-object-title"
                                onClick={() => setIsDetailOpen(true)}
                            >
                                {title}
                            </button>
                        ) : isBand && bandPath ? (
                            <button
                                type="button"
                                className="post-card__related-object-title"
                                onClick={handleOpenBand}
                            >
                                {title}
                            </button>
                        ) : (
                            <h3 className="timeline-entity-card__title">{title}</h3>
                        )}
                        <div className="post-card__related-object-meta">
                            {startsAtLabel && (
                                <span>{t('home.postCard.relatedObject.startsAt', { defaultValue: 'Starts {{value}}', value: startsAtLabel })}</span>
                            )}
                            {locationLine && <span>{locationLine}</span>}
                            {isVenue && Number.isFinite(item?.capacity) && (
                                <span>{t('common.capacity', { defaultValue: 'Capacity {{value}}', value: item.capacity })}</span>
                            )}
                        </div>
                        {isScheduled && (
                            <div className="post-card__related-object-attendance">
                                {t('home.postCard.relatedObject.attendanceSummary', {
                                    going: attendanceSummary.going,
                                    interested: attendanceSummary.interested,
                                    pending: attendanceSummary.pending,
                                    defaultValue: '{{going}} going · {{interested}} interested · {{pending}} pending',
                                })}
                            </div>
                        )}
                        {viewerAttendanceStatus === 'PENDING' && (
                            <p className="post-card__related-object-hint">
                                {t('home.postCard.relatedObject.pendingApproval')}
                            </p>
                        )}
                        {isVenue && Array.isArray(item?.amenities) && item.amenities.length > 0 && (
                            <div className="post-card__related-object-attendance">
                                {item.amenities.join(' · ')}
                            </div>
                        )}
                        <div className="post-card__related-object-actions" role="group" aria-label={t('home.postCard.relatedObject.actionsLabel', 'Actions')}>
                            {isScheduled && RSVP_ACTIONS.map(({ status, translationKey }) => (
                                <button
                                    key={status}
                                    type="button"
                                    className={`post-card__related-object-rsvp${activeRsvpStatus === status ? ' is-active' : ''}`}
                                    onClick={() => handleRsvpAction(status)}
                                    disabled={!canMutateRsvp || isRsvpSubmitting}
                                >
                                    {t(translationKey, status)}
                                </button>
                            ))}
                            {isScheduled && (
                                <button
                                    type="button"
                                    className="post-card__related-object-details"
                                    onClick={() => setIsDetailOpen(true)}
                                >
                                    {t('home.postCard.relatedObject.actions.viewDetails', 'View details')}
                                </button>
                            )}
                            {isVenue && item?.website && (
                                <a
                                    className="post-card__related-object-details timeline-entity-card__link"
                                    href={item.website}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {t('discover.table.fields.website', 'Website')}
                                </a>
                            )}
                            {isBand && bandPath && (
                                <button
                                    type="button"
                                    className="post-card__related-object-details"
                                    onClick={handleOpenBand}
                                >
                                    {t('bands.common.viewBand', 'View band')}
                                </button>
                            )}
                        </div>
                        {isScheduled && !readOnly && !canMutateRsvp && (
                            <p className="post-card__related-object-hint">
                                {t(
                                    'home.postCard.relatedObject.rsvpDisabled',
                                    'RSVP is available only for published events and sessions.',
                                )}
                            </p>
                        )}
                        {rsvpError && (
                            <p className="post-card__related-object-error" role="alert">
                                {rsvpError}
                            </p>
                        )}
                    </section>
                </div>
                {socialFooter ? (
                    <footer className="timeline-entity-card__social-footer">
                        {socialFooter}
                    </footer>
                ) : null}
            </FrostedPanel>
            {isScheduled && (
                <ScheduledEntityDetailModal
                    isOpen={isDetailOpen}
                    objectType={item.objectType}
                    objectId={item.id}
                    initialSummary={detailInitialSummary}
                    initialAttendance={attendance}
                    onClose={() => setIsDetailOpen(false)}
                    onAttendanceChange={setAttendance}
                    readOnly={readOnly}
                />
            )}
            <ConfirmDialog
                isOpen={isDeleteDialogOpen}
                title={t('home.timelineEntityCard.deleteDialog.title')}
                message={deleteDialogMessage}
                confirmLabel={t('home.timelineEntityCard.deleteDialog.confirm')}
                cancelLabel={t('common.cancel')}
                processingLabel={t('home.timelineEntityCard.deleteDialog.processing')}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                isProcessing={isDeleting}
                error={deleteError || undefined}
                confirmVariant="danger"
            />
        </>
    );
}

TimelineEntityCard.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.string.isRequired,
        objectType: PropTypes.oneOf(['EVENT', 'SESSION', 'VENUE', 'BAND']).isRequired,
        ownerId: PropTypes.string,
        canManage: PropTypes.bool,
        feedReasonCodes: PropTypes.arrayOf(PropTypes.string),
        reasonCodes: PropTypes.arrayOf(PropTypes.string),
        suggestionKind: PropTypes.string,
        timelineItemKind: PropTypes.string,
        visibility: PropTypes.string,
        createdAt: PropTypes.string,
        title: PropTypes.string,
        name: PropTypes.string,
        handle: PropTypes.string,
        publicHandle: PropTypes.string,
        description: PropTypes.string,
        status: PropTypes.string,
        startAt: PropTypes.string,
        endAt: PropTypes.string,
        timezone: PropTypes.string,
        joinPolicy: PropTypes.string,
        attendeeLimit: PropTypes.number,
        viewerAttendanceStatus: PropTypes.string,
        viewerInvitedByOrganizer: PropTypes.bool,
        organizerInviteCount: PropTypes.number,
        attendanceSummary: PropTypes.shape({
            going: PropTypes.number,
            interested: PropTypes.number,
            pending: PropTypes.number,
        }),
        organizer: PropTypes.shape({
            objectType: PropTypes.string,
            objectId: PropTypes.string,
            name: PropTypes.string,
            profilePicture: PropTypes.string,
        }),
        venue: PropTypes.shape({
            objectId: PropTypes.string,
            name: PropTypes.string,
            sourceType: PropTypes.string,
            inactive: PropTypes.bool,
            inactiveAt: PropTypes.string,
        }),
        author: PropTypes.shape({
            displayName: PropTypes.string,
            username: PropTypes.string,
            profilePicture: PropTypes.string,
        }),
        location: PropTypes.shape({
            locationName: PropTypes.string,
            addressLine: PropTypes.string,
            city: PropTypes.string,
            stateOrRegion: PropTypes.string,
            country: PropTypes.string,
            countryCode: PropTypes.string,
            latitude: PropTypes.number,
            longitude: PropTypes.number,
        }),
        locationName: PropTypes.string,
        addressLine: PropTypes.string,
        city: PropTypes.string,
        stateOrRegion: PropTypes.string,
        country: PropTypes.string,
        countryCode: PropTypes.string,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        updatedAt: PropTypes.string,
        avatarUrl: PropTypes.string,
        imageUrl: PropTypes.string,
        profilePicture: PropTypes.string,
        website: PropTypes.string,
        amenities: PropTypes.arrayOf(PropTypes.string),
        capacity: PropTypes.number,
    }).isRequired,
    currentUserId: PropTypes.string,
    dropdownOpenId: PropTypes.string,
    onToggleDropdown: PropTypes.func,
    onDeleteEntity: PropTypes.func,
    onEditEntity: PropTypes.func,
    readOnly: PropTypes.bool,
    socialFooter: PropTypes.node,
};
